import { Table } from '@/components';
import { loadingTableHandler } from '@/utils/handler/loadingTableHandler';
import { Button, Dropdown, MenuProps, Tag, Typography } from 'antd';
import { ColumnType } from 'antd/es/table';
import React from 'react';
import { IoMailOutline, IoSendOutline } from 'react-icons/io5';
import { MdMoreVert, MdOutlineEdit, MdOutlineBlock, MdOutlineCheckCircle, MdOutlineLocalOffer } from 'react-icons/md';
import { AccountStatusEnum } from '../constants';
import { IOnboardingAccount } from '../types';
import { SetupProgressBar } from './SetupProgressBar';

const { Text } = Typography;

interface OnboardingTableProps {
  records: IOnboardingAccount[];
  onEdit: (record: IOnboardingAccount) => void;
  onViewEmail: (record: IOnboardingAccount) => void;
  onSendReminder: (record: IOnboardingAccount) => void;
  onDeactivate: (record: IOnboardingAccount) => void;
  onActivate: (record: IOnboardingAccount) => void;
  onConfigureDiscount: (record: IOnboardingAccount) => void;
  isLoading?: boolean;
  canUpdateOnboarding?: boolean;
  current?: number;
  pageSize?: number;
  total?: number;
  nextLink?: string;
  totalRecordCountLimitExceeded?: boolean;
  handleChange?: (page: number, pageSize: number) => void;
}

export const OnboardingTable: React.FC<OnboardingTableProps> = ({
  records,
  onEdit,
  onViewEmail,
  onSendReminder,
  onDeactivate,
  onActivate,
  onConfigureDiscount,
  isLoading = false,
  canUpdateOnboarding = true,
  current,
  pageSize,
  total,
  nextLink,
  totalRecordCountLimitExceeded,
  handleChange,
}) => {
  const columns: ColumnType<IOnboardingAccount>[] = [
    {
      title: 'Company',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: IOnboardingAccount) => (
        <div>
          <Text strong>{text}</Text>
          {record.dogma_tradingname && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                T/A: {record.dogma_tradingname}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'customertypecode@OData.Community.Display.V1.FormattedValue',
      key: 'customertypecode',
      render: (customertypecode) => (
        <Tag>{customertypecode}</Tag>
      ),
    },
    {
      title: 'Sage Number',
      dataIndex: 'dogma_prefix',
      key: 'dogma_prefix',
      render: (prefix: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {prefix || 'N/A'}
        </Text>
      ),
    },
    {
      title: 'Contact Email',
      dataIndex: ["primarycontactid", "emailaddress1"],
      key: 'emailaddress1',
      render: (text: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {text || 'N/A'}
        </Text>
      ),
    },
    {
      title: 'Phone',
      dataIndex: ["primarycontactid", "telephone1"],
      key: 'telephone1',
      render: (text: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {text || 'N/A'}
        </Text>
      ),
    },
    {
      title: 'Credit Terms',
      dataIndex: 'paymenttermscode@OData.Community.Display.V1.FormattedValue',
      key: 'paymenttermscode',
    },
    {
      title: 'Currency',
      key: 'dealerscurrency',
      render: (_: any, record: IOnboardingAccount) => {
        const label = record['_dogma_dealerscurrency_value@OData.Community.Display.V1.FormattedValue'];
        return label ? <Tag color="geekblue">{label}</Tag> : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Region',
      dataIndex: 'dogma_region@OData.Community.Display.V1.FormattedValue',
      key: 'dogma_region',
    },
    {
      title: 'Status',
      dataIndex: 'statuscode',
      key: 'statuscode',
      render: (status: number, record) => {
        if (record.statecode === 1) {
          return <Tag color="red" style={{ fontWeight: 500 }}>Deleted</Tag>;
        }

        let color = 'default';
        if (status === AccountStatusEnum.ACCOUNT_CREATED) {
          color = 'blue';
        } else if (status === AccountStatusEnum.SETUP_IN_PROGRESS) {
          color = 'orange';
        } else if (status === AccountStatusEnum.SETUP_COMPLETE) {
          color = 'green';
        }

        return (
          <Tag color={color} style={{ fontWeight: 500 }}>
            {record['statuscode@OData.Community.Display.V1.FormattedValue'] || 'Unknown'}
          </Tag>
        );
      },
    },
    {
      title: 'Setup Progress',
      dataIndex: 'dogma_progress',
      key: 'dogma_progress',
      width: 200,
      render: (progress: number) => (
        <SetupProgressBar progress={progress || 0} />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center' as const,
      render: (_: any, record: IOnboardingAccount) => {
        const isInactive = record.statecode === 1;
        const menuItems: MenuProps['items'] = [];

        if (canUpdateOnboarding) {
          menuItems.push({
            key: 'edit',
            label: 'Edit Onboarding',
            icon: <MdOutlineEdit />,
            onClick: () => onEdit(record),
          });
        }
        menuItems.push({
          key: 'email',
          label: 'View Welcome Email',
          icon: <IoMailOutline />,
          onClick: () => onViewEmail(record),
        });
        if (canUpdateOnboarding) {
          menuItems.push({
            key: 'reminder',
            label: 'Send Reminder',
            icon: <IoSendOutline />,
            onClick: () => onSendReminder(record),
          });
        }
        if (canUpdateOnboarding && !isInactive) {
          menuItems.push({
            key: 'configure-discount',
            label: 'Configure Discount',
            icon: <MdOutlineLocalOffer />,
            onClick: () => onConfigureDiscount(record),
          });
        }
        if (canUpdateOnboarding && !isInactive) {
          menuItems.push({ type: 'divider' });
          menuItems.push({
            key: 'deactivate',
            label: 'Delete Account',
            icon: <MdOutlineBlock />,
            danger: true,
            onClick: () => onDeactivate(record),
          });
        }
        if (canUpdateOnboarding && isInactive) {
          menuItems.push({ type: 'divider' });
          menuItems.push({
            key: 'activate',
            label: 'Activate Account',
            icon: <MdOutlineCheckCircle />,
            onClick: () => onActivate(record),
          });
        }

        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button type="text" icon={<MdMoreVert />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div>
      {
        loadingTableHandler(isLoading, <Table
          isLoading={isLoading}
          columns={columns}
          dataSource={records}
          rowKey="accountid"
          current={current}
          pageSize={pageSize}
          total={total}
          nextLink={nextLink}
          totalRecordCountLimitExceeded={totalRecordCountLimitExceeded}
          handleChange={handleChange}
        />)
      }
    </div>
  );
};
